export * from './components';
export * from './adapters';
export * from '@omegagrid/tree';
export * from '@omegagrid/toolbar';

import { ComponentId } from '@omegagrid/core';
import rp, { AppComponent, Application, Container, CreateComponentOptions, SqlEditor, parseComponentId } from '@reporting/rp-app-host';
import { componentFactories } from './factories';
import { ModuleCode } from '@reporting/rp-types/accounting';
import { FilterEvent } from './components/filters/filter';
import { ContextData } from '@reporting/rp-types/analytics';
import { ReportContainer } from './components/reportContainer';
import { Sidebar } from './components/sidebar';
import { FilterContainer } from './components/filters/filterContainer';
import { ReportBroker, ContextBroker, ReportStore } from './model';
import { ReportContext } from '@reporting/rp-types/analytics';
import { BucketEvent } from '@omegagrid/bucket';
import { debounce } from 'ts-debounce';
import { ModuleDefinition } from '@reporting/rp-types/definition';
import { ReportSearchProvider } from './searchProvider';
import { Resource } from "@reporting/rp-types/ext";

/**
 * Top-level report containers share one component id for every context
 * type: `analytics-reportContainer-<id_context>`. 'dashboardContainer' is a
 * legacy alias (see factories.ts) still accepted for persisted tab ids.
 */
const REPORT_CONTAINER_TYPES = ['reportContainer', 'dashboardContainer'];

/** id_context of a top-level report container component id, null for anything else */
export const getContextIdFromComponentId = (componentId: ComponentId) => {
	if (!componentId) return null;
	const id = parseComponentId(componentId);
	if (id.type != 'analytics' || !REPORT_CONTAINER_TYPES.includes(id.id as string)) return null;
	return parseInt(id.ids[1]) || null;
}

export class AnalyticsApplication extends Application {

	modules = new Map<ModuleCode, ModuleDefinition>();
	resources = new Map<ModuleCode, Resource[]>();
	reportContainers = new Map<number, ReportContainer>();
	activeIdContext: number;
	reportBroker: ReportBroker;
	contextBroker: ContextBroker;

	/**
	 * One ReportStore per open multi context (dashboard), keyed by id_context.
	 * The store draft is the single live version of the context; the broker
	 * cache stays pristine. See REFACTORING-context-state.md, section 2.1.
	 */
	private _reportStores = new Map<number, ReportStore>();

	getReportStore(idContext: number): ReportStore {
		return (idContext && this._reportStores.get(idContext)) || null;
	}

	getOrCreateReportStore(context: ReportContext): ReportStore {
		if (!context?.id_context) return null;
		let store = this._reportStores.get(context.id_context);
		if (!store) {
			store = new ReportStore(context);
			this._reportStores.set(context.id_context, store);
		}
		return store;
	}

	dropReportStore(idContext: number) {
		this._reportStores.delete(idContext);
	}

	/** the saved context got a new id (save as new), move the store under it */
	rekeyReportStore(oldId: number, newId: number) {
		if (oldId === newId) return;
		const store = this._reportStores.get(oldId);
		if (!store) return;
		this._reportStores.delete(oldId);
		this._reportStores.set(newId, store);
	}

	/**
	 * An open report got a new id (save as new renames the host component
	 * through updateComponent, which fires no activate event). Keep every
	 * id-keyed bookkeeping in sync: the store, the container map, the active
	 * id (the replace target of "Create view") and the sidebar filter that
	 * follows the active report.
	 */
	renameReportContainer(oldId: number, newId: number) {
		if (!oldId || !newId || oldId === newId) return;
		this.rekeyReportStore(oldId, newId);

		const container = this.reportContainers.get(oldId);
		if (container) {
			this.reportContainers.delete(oldId);
			this.reportContainers.set(newId, container);
		}

		if (this.activeIdContext === oldId) this.activeIdContext = newId;

		// raw map lookup - components.get() would create the sidebar when missing
		const sidebar = this.host?.components?.components.get('analytics') as Sidebar;
		const filters = sidebar?.innerComponent;
		if (filters instanceof FilterContainer) filters.renameContext(oldId, newId);
	}

	async register(container: Container) {
		super.register(container);
		container.services.register('analytics');
		container.commands.registerProvider(new ReportSearchProvider());
	}

	_onFilterPreview = (e: FilterEvent) => this.updatePreview(e.previewContextData);
	_onFilterPreviewStop = () => this.updatePreview();

	updatePreview = debounce((contextData?: Partial<ContextData>) => {
		if (contextData) this.getActiveReportContainer()?.previewReport(contextData);
		else this.getActiveReportContainer()?.resetReport();
	}, 100)

	removeContainer(idContext: number) {
		this.reportContainers.delete(idContext);
	}

	async init(container: Container) {
		await super.init(container);

		setTimeout(() => {
			if (!this.host?.bucket?.activeComponentId) return;
			this.activeIdContext = getContextIdFromComponentId(this.host.bucket.activeComponentId);
		}, 1000);

		container.services.register('analytics');
		container.services.register('definition');
		container.services.register('extensions');

		this.reportBroker = new ReportBroker(this);
		this.contextBroker = new ContextBroker();

		const modules = await rp.ms('definition').get<ModuleDefinition[]>('modules');
		this.modules = new Map(modules.map(module => [module.module as ModuleCode, module]));

		const resources = await rp.ms('extensions').get<Resource[]>('userResources');
		resources.forEach(res => {
			if (!this.resources.has(res.module as ModuleCode)) {
				this.resources.set(res.module as ModuleCode, []);
			}
			this.resources.get(res.module as ModuleCode).push(res);
		});

		this.host.addEventListener('filter.preview', this._onFilterPreview);
		this.host.addEventListener('filter.stoppreview', this._onFilterPreviewStop);

		this.host.addEventListener('bucket.activate', (e: BucketEvent) => {
			if (!e.componentId) return;
			this.activeIdContext = getContextIdFromComponentId(e.componentId);
		});

		this.host.addEventListener('bucket.close', (e: BucketEvent) => {
			const idContext = getContextIdFromComponentId(e.componentId);
			if (idContext) this.removeContainer(idContext);
		});
	}

	openPath(path: string) {
		const segments = path.split('/');
		if (segments.length == 0) return;
		if (!isNaN(parseInt(segments[0]))) {
			this.host.open(`analytics-reportContainer-${segments[0]}`, {
				title: `#${segments[0]}`,
			});
		}
	}

	async createComponent(id: ComponentId, options?: CreateComponentOptions): Promise<AppComponent> {
		const parsedId = parseComponentId(id);
		const factory = componentFactories.get(parsedId.id as string || parsedId.type);
		if (!factory) return null;
		const component = await factory(parsedId, options);

		const idContext = getContextIdFromComponentId(id);
		if (idContext) {
			this.reportContainers.set(idContext, component as ReportContainer);
			if (this.activeIdContext == null) this.activeIdContext = idContext;
		}

		return component as AppComponent;
	}

	/** the open top-level container of a context, null when not open (never creates one) */
	getReportContainer(id: number): ReportContainer {
		if (!id) return null;
		// raw map lookup - components.get() would create a missing component
		const components = this.host?.components?.components;
		if (!components) return null;
		for (const type of REPORT_CONTAINER_TYPES) {
			const container = components.get(`analytics-${type}-${id}`);
			if (container) return container as ReportContainer;
		}
		return null;
	}

	getActiveReportContainer(): ReportContainer {
		return this.activeIdContext ? this.getReportContainer(this.activeIdContext) : null;
	}

}
